"use client";

import type { useChat } from "@ai-sdk/react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import {
  type QueryGroupPatternType,
  QueryGroupPatternTypeLabels,
} from "../shared/query-group-pattern-type";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { CreateSaQueryGroupInput } from "./schema";

const ProjectDataQuery = graphql(`
  query SaQueryGroupConfirmProjectData($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsProject {
        name
        searchAnalytics { id __typename }
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function CreateSaQueryGroupConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const isTarget =
    isToolUIPart(part) &&
    getToolName(part) === "create_search_analytics_query_group";
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<CreateSaQueryGroupInput>;
  const projectId = input.projectId ?? "";

  const [{ data, fetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectId },
    pause: !(isTarget && isInputAvailable && projectId),
  });

  const projectName =
    data?.node?.__typename === "SearchAnalyticsProject"
      ? data.node.name
      : undefined;

  if (!isTarget || !isInputAvailable) return null;

  const patternTypeLabel = QueryGroupPatternTypeLabels as Record<
    QueryGroupPatternType,
    unknown
  >;

  const patterns = input.patterns ?? [];
  const patternCount = patterns.length;
  const previewLimit = 10;
  const patternPreview = patterns.slice(0, previewLimit);
  const remainingCount = Math.max(patternCount - previewLimit, 0);

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to create a new Search Analytics Query Group?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>Name</Trans>: <span className="font-medium">{input.name}</span>
        </li>
        {input.description ? (
          <li>
            <Trans>Description</Trans>:{" "}
            <span className="font-normal">{input.description}</span>
          </li>
        ) : null}
        <li>
          <Trans>Operator</Trans>:{" "}
          <span className="font-medium">{input.op}</span>
        </li>
        {input.prepend !== undefined ? (
          <li>
            <Trans>Prepend</Trans>:{" "}
            <span className="font-medium">
              {input.prepend ? "true" : "false"}
            </span>
          </li>
        ) : null}
        <li className="space-y-2">
          <div className="flex items-center gap-2">
            <Trans>Patterns</Trans>:{" "}
            <span className="font-medium">
              <Trans>{patternCount} patterns</Trans>
            </span>
            {remainingCount > 0 ? (
              <span className="text-muted-foreground">
                <Trans>showing first {previewLimit}</Trans>
              </span>
            ) : null}
          </div>
          {patternPreview.length > 0 ? (
            <div className="space-y-1 rounded-md border p-2">
              {patternPreview.map((p, idx) => (
                <div
                  key={`${p.type}-${p.pattern}-${idx}`}
                  className="flex items-start gap-2"
                >
                  <Badge variant="secondary" className="shrink-0">
                    {i18n._(
                      patternTypeLabel[
                        p.type as QueryGroupPatternType
                      ] as Parameters<typeof i18n._>[0],
                    ) ?? p.type}
                  </Badge>
                  <span className="break-all font-mono text-xs">
                    {p.pattern}
                  </span>
                </div>
              ))}
              {remainingCount > 0 ? (
                <div className="text-muted-foreground text-xs">
                  <Trans>{remainingCount} more not shown</Trans>
                </div>
              ) : null}
            </div>
          ) : null}
        </li>
        {projectId ? (
          <li>
            <Trans>Project</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-12" />
            ) : (
              <span className="font-medium">{projectName ?? "Unknown"}</span>
            )}
          </li>
        ) : null}
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Create</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
